// Styles are required for the map (e.g. attribution) to work properly
import 'mapbox-gl/dist/mapbox-gl.css';

import { MapPin } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Point } from 'geojson';
import React, { Fragment, useMemo } from 'react';
import ReactMapGL, { AttributionControl, Layer, Marker, Source } from 'react-map-gl';
import styled, { useTheme } from 'styled-components';

import { DeliveryAreaValue } from 'apis/delivery-settings-api/types';
import { typographyCss } from 'common/typography';

import MapResizeListener from './MapResizeListener';
import MapZoomButtons from './MapZoomButtons';
import { disabledAreaColor, getAreaColor } from './areaColors';
import { fitBoundsOptions, getBounds, mapStyles } from './mapUtils/mapUtils';

export type MapData = Omit<DeliveryAreaValue, 'deliveryFee' | 'minBasketSize'>[];
type DeliveryAreaMapProps = {
  className?: string;
  deliveryAreas: MapData;
  venueLocation?: Point;
};

const Root = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  margin-block-end: 16px;

  .mapboxgl-canvas {
    border: 1px solid ${cbSemanticCssVars.borderPrimary};
    border-radius: 8px;
  }
`;

const MapPinIcon = styled(MapPin)`
  fill: ${colors.wolt100};
  pointer-events: none;
  width: 30px;
  height: 30px;
`;

const Label = styled.div<{ bgColor: string }>`
  background-color: ${({ bgColor }) => bgColor};
  color: ${cssThemedValue({ light: colors.salt100, dark: colors.pepper100 })};
  max-width: 160px;
  height: 20px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  border-radius: 8px;
  ${typographyCss.Overline4};
  font-weight: 500;
  padding: 4px 8px;
`;

const DeliveryAreaMap = ({ className, deliveryAreas, venueLocation }: DeliveryAreaMapProps) => {
  const theme = useTheme();
  const { cbDirection, cbThemeMode } = theme;

  const bounds = useMemo(() => getBounds(deliveryAreas), [deliveryAreas]);
  if (!bounds || !process.env.MAPBOX_ACCESS_TOKEN) {
    return <div className={className} />;
  }

  const [venueLongitude, venueLatitude] = venueLocation?.coordinates ?? [];

  return (
    <Root className={className}>
      <ReactMapGL
        dragRotate={false}
        attributionControl={false}
        mapboxAccessToken={process.env.MAPBOX_ACCESS_TOKEN}
        logoPosition={cbDirection === 'rtl' ? 'bottom-right' : 'bottom-left'}
        maxPitch={0}
        maxZoom={16}
        mapStyle={mapStyles[cbThemeMode]}
        initialViewState={{ bounds, fitBoundsOptions }}
      >
        <MapResizeListener />
        {deliveryAreas.map(
          ({ id, geoRange, disabled, pricingCircles }, index) =>
            !!geoRange && (
              <Fragment key={id}>
                {!!pricingCircles?.length && (
                  <Source
                    id={`distance-pricing-fill-${id}`}
                    type="geojson"
                    data={{ type: 'FeatureCollection', features: pricingCircles }}
                  >
                    <Layer
                      type="line"
                      paint={{
                        'line-width': 1.5,
                        'line-color': disabledAreaColor[cbThemeMode],
                        'line-opacity': 0.5,
                      }}
                    />
                  </Source>
                )}
                <Source
                  id={id}
                  type="geojson"
                  data={{
                    type: 'FeatureCollection',
                    features: [
                      {
                        type: 'Feature',
                        geometry: {
                          type: 'Polygon',
                          coordinates:
                            geoRange?.coordinates.map(coords => coords.map(c => [c.longitude, c.latitude])) || [],
                        },
                        properties: {},
                      },
                    ],
                  }}
                >
                  <Layer
                    type="fill"
                    paint={{
                      'fill-color': disabled ? disabledAreaColor[cbThemeMode] : getAreaColor(index),
                      'fill-opacity': 0.15,
                    }}
                  />
                  <Layer
                    type="line"
                    paint={
                      disabled
                        ? {
                            'line-width': 1.5,
                            'line-color': disabledAreaColor[cbThemeMode],
                            'line-opacity': 0.5,
                            'line-dasharray': [2, 1],
                          }
                        : {
                            'line-width': 1.5,
                            'line-color': getAreaColor(index),
                            'line-opacity': 1,
                          }
                    }
                  />
                </Source>
              </Fragment>
            ),
        )}
        {venueLongitude !== undefined && venueLatitude !== undefined && (
          <Marker anchor="bottom" latitude={venueLatitude} longitude={venueLongitude}>
            <MapPinIcon />
          </Marker>
        )}
        {
          // Add labels separately to have them on top of all area lines and fills
          deliveryAreas.map(
            ({ id, name, labelPosition, disabled }, index) =>
              !!labelPosition && (
                <Marker key={`label-${id}`} {...labelPosition} anchor="center">
                  <Label bgColor={disabled ? disabledAreaColor[cbThemeMode] : getAreaColor(index)} dir="auto">
                    {name}
                  </Label>
                </Marker>
              ),
          )
        }
        <AttributionControl compact position={cbDirection === 'rtl' ? 'bottom-left' : 'bottom-right'} />
        <MapZoomButtons position={cbDirection === 'rtl' ? 'top-right' : 'top-left'} bounds={bounds} />
      </ReactMapGL>
    </Root>
  );
};

export default DeliveryAreaMap;
